import React from 'react';
import { Search, Users, ClipboardCheck, Rocket } from 'lucide-react';
import { hiringProcess } from '../data/mock';
import { useTheme } from '../context/ThemeContext';

const stepIcons = [Search, Users, ClipboardCheck, Rocket];

const ProcessSection = () => {
  const { theme } = useTheme();

  return (
    <section id="process" className={`relative py-16 sm:py-20 lg:py-24 transition-colors duration-300 ${
      theme === 'dark' ? 'bg-[#0a0a0a]' : 'bg-white'
    }`}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-[7.6923%]">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-12 lg:mb-14">
          <div className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 mb-4 sm:mb-6 border ${
            theme === 'dark' ? 'border-white/20 bg-white/5' : 'border-gray-200 bg-gray-50'
          }`}>
            <span className={`text-xs sm:text-sm font-medium tracking-wide uppercase ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>How It Works</span>
          </div>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Our Simple Process
          </h2>
          <p className={`text-base sm:text-lg max-w-[600px] mx-auto ${
            theme === 'dark' ? 'text-white/60' : 'text-gray-600'
          }`}>
            A streamlined approach to connecting you with the right IT talent.
          </p>
        </div>

        {/* Process Steps */}
        <div className="relative">
          {/* Connection Line - Desktop */}
          <div className={`hidden lg:block absolute top-20 left-[12.5%] right-[12.5%] h-[2px] ${
            theme === 'dark' ? 'bg-white/10' : 'bg-gray-200'
          }`}>
            <div className={`absolute inset-0 ${
              theme === 'dark' 
                ? 'bg-gradient-to-r from-[#00FFD1]/50 via-[#00FFD1] to-[#00FFD1]/50' 
                : 'bg-gradient-to-r from-[#00997A]/50 via-[#00997A] to-[#00997A]/50'
            }`} />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {hiringProcess.map((step, index) => {
              const Icon = stepIcons[index];
              return (
                <div key={step.step} className="relative">
                  <div className="flex flex-col items-center text-center group">
                    {/* Step Number & Icon */}
                    <div className="relative mb-4 sm:mb-6">
                      <div className={`w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 flex items-center justify-center border-2 transition-colors ${
                        theme === 'dark' 
                          ? 'bg-black border-[#00FFD1] group-hover:bg-[#00FFD1]/10' 
                          : 'bg-white border-[#00997A] group-hover:bg-[#00997A]/10'
                      }`}>
                        <Icon className={`w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 ${
                          theme === 'dark' ? 'text-[#00FFD1]' : 'text-[#00997A]'
                        }`} />
                      </div>
                      {/* Step Number Badge */}
                      <div className={`absolute -top-2 -right-2 w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 flex items-center justify-center font-semibold text-xs sm:text-sm ${
                        theme === 'dark' 
                          ? 'bg-[#00FFD1] text-black' 
                          : 'bg-[#00997A] text-white'
                      }`}>
                        {step.step}
                      </div>
                    </div>

                    {/* Content */}
                    <h3 className={`text-sm sm:text-base lg:text-lg font-semibold mb-2 ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {step.title}
                    </h3>
                    <p className={`text-xs sm:text-sm leading-relaxed ${
                      theme === 'dark' ? 'text-white/60' : 'text-gray-600'
                    }`}>
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
